// @ts-nocheck
import {
  COMING_SOON_DESC,
  ITEMS,
  PASSIVE_TREES,
  SPELLS,
  SPELL_DEBUFFS,
  SPELL_SCHOOLS,
  SPELL_CATEGORIES,
  UPGRADE,
  activeSynergyForSpell,
  keyLabelForSlot,
  shopCategoryLabel,
  slotOfSpell,
  passiveRequiresLabel,
  spellAvail,
  spellDebuffTags,
  spellDebuffTagsForTip,
  spellDescDetail,
  spellStatsForTip,
  synergyHostStats,
  passiveOwned,
  passiveShopName,
  capitalizeDisplayName,
  TITLE_SUFFIX,
} from "./data";
import {
  TAU,
  rgba,
} from "./util";
import {
  Sound,
} from "./audio";
import {
  barIconInner,
  spellIconInner,
  synergyIconInner,
  synergyOrbInner,
} from "./spell-icons";

/* ---------------- networking ---------------- */
export function turnToward(cur, target, maxStep) {
  let d = angleDiff(cur, target);
  if (d > maxStep) d = maxStep;
  if (d < -maxStep) d = -maxStep;
  return cur + d;
}
export function angleDiff(from, to) {
  let d = to - from;
  while (d > Math.PI) d -= TAU;
  while (d < -Math.PI) d += TAU;
  return d;
}
export function angLerp(a, b, t) {
  let d = b - a;
  while (d > Math.PI) d -= TAU;
  while (d < -Math.PI) d += TAU;
  return a + d * t;
}
export function spellLabel(id) {
  if (!id) return "Unknown";
  const s = SPELLS[id];
  return s ? s.name : id;
}

export function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

/** HTML player name with optional gold cosmetic title spans and the
 * "void-touched" purple gradient for admins. */
export function playerNameHTML(name, title, titlePos, admin) {
  let n = escapeHtml(capitalizeDisplayName(name || ""));
  if (admin) n = `<span class="admin-name">${n}</span>`;
  if (!title) return n;
  const t = escapeHtml(title);
  if (titlePos === TITLE_SUFFIX) return `${n}<span class="player-title"> ${t}</span>`;
  return `<span class="player-title">${t} </span>${n}`;
}

// Presentation helpers for the match-history view.
export function modeLabel(mode) {
  if (mode === "allrandom") return "All Random";
  if (mode === "skilldraft") return "Skill Draft";
  if (mode === "pve") return "PvE";
  return "Classic";
}
export function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString();
}

/* ---------------- shared hover tooltips ---------------- */
// One floating element reused everywhere (shop, All Random reveal, lobby list,
// and the on-canvas action bar). Built from the same SPELLS/ITEMS tables so the
// numbers stay in sync with the server catalog.
export const Tip = {
  el: null,
  ensure() {
    if (!this.el) {
      this.el = document.createElement("div");
      this.el.className = "tooltip hidden";
      document.body.appendChild(this.el);
    }
    return this.el;
  },
  show(html, x, y, opts) {
    if (!html) { this.hide(); return; }
    const el = this.ensure();
    el.innerHTML = html;
    el.classList.remove("hidden");
    this._opts = opts || null;
    this.move(x, y, opts);
  },
  move(x, y, opts) {
    const el = this.el;
    if (!el || el.classList.contains("hidden")) return;
    const o = opts || this._opts || {};
    const r = el.getBoundingClientRect();
    const offX = 16, offY = 18;
    let nx = x + offX;
    let ny = y + offY;
    if (o.preferAbove) {
      ny = y - r.height - offY;
    } else if (ny + r.height > window.innerHeight - 8) {
      ny = y - r.height - offY;
    }
    if (nx + r.width > window.innerWidth - 8) nx = x - r.width - offX;
    if (ny + r.height > window.innerHeight - 8) ny = y - r.height - offY;
    if (ny < 8) ny = y + offY;
    if (nx < 8) nx = 8;
    el.style.left = nx + "px";
    el.style.top = ny + "px";
  },
  hide() {
    cancelTipMove();
    if (this.el) this.el.classList.add("hidden");
    this._opts = null;
  },
  isOver(x, y) {
    const el = this.el;
    if (!el || el.classList.contains("hidden")) return false;
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  },
};

let _tipMoveRaf = null;
let _tipMovePos = null;

export function cancelTipMove() {
  if (_tipMoveRaf) {
    cancelAnimationFrame(_tipMoveRaf);
    _tipMoveRaf = null;
  }
  _tipMovePos = null;
}

/** Coalesce tooltip reposition to one layout read per frame. */
export function scheduleTipMove(x, y, opts) {
  _tipMovePos = { x, y, opts };
  if (_tipMoveRaf) return;
  _tipMoveRaf = requestAnimationFrame(() => {
    _tipMoveRaf = null;
    const p = _tipMovePos;
    if (!p) return;
    Tip.move(p.x, p.y, p.opts);
  });
}

export function schoolTipHTML(school) {
  const meta = SPELL_SCHOOLS[school];
  if (!meta) return "";
  const c = meta.color;
  return `<span class="tip-school" style="color:${c};border-color:${c}55;background:${c}18" title="Damage school vs PvE bosses">${escapeHtml(meta.label)}</span>`;
}

export function categoryTipHTML(category) {
  const meta = SPELL_CATEGORIES[category];
  if (!meta) return "";
  const c = meta.color;
  return `<span class="tip-category" style="color:${c};border-color:${c}55;background:${c}18" title="Spell category">${escapeHtml(meta.label)}</span>`;
}

const DEBUFF_SCOPE_ORDER = ["both", "self", "enemy"];

export function debuffTipHTML(tags) {
  if (!tags || !tags.length) return "";
  const grouped = { both: [], self: [], enemy: [] };
  for (const tag of tags) {
    const meta = SPELL_DEBUFFS[tag.id];
    if (!meta || !grouped[tag.scope]) continue;
    grouped[tag.scope].push(meta);
  }
  const rows = [];
  for (const scope of DEBUFF_SCOPE_ORDER) {
    const items = grouped[scope];
    if (!items.length) continue;
    const pills = items.map((meta) => {
      const c = meta.color;
      return `<span class="tip-debuff" style="color:${c};border-color:${c}55;background:${c}18">${escapeHtml(meta.label)}</span>`;
    }).join("");
    rows.push(`<span class="tip-debuff-scope"><b>${scope}:</b> ${pills}</span>`);
  }
  if (!rows.length) return "";
  return `<div class="tip-debuff-row">${rows.join("")}</div>`;
}

export function debuffEffectsHTML(tags) {
  if (!tags || !tags.length) return "";
  const rows = tags.map((tag) => {
    const meta = SPELL_DEBUFFS[tag.id];
    if (!meta) return "";
    const head = `<span class="tip-effect-head"><b>${escapeHtml(tag.scope)}:</b> ${escapeHtml(meta.label)}</span>`;
    const detail = tag.detail
      ? `<span class="tip-effect-detail">${escapeHtml(tag.detail)}</span>`
      : "";
    return `<div class="tip-effect-row">${head}${detail}</div>`;
  }).filter(Boolean).join("");
  if (!rows) return "";
  return `<div class="tip-effects">${rows}</div>`;
}

export function buildTip({ icon, iconHTML, name, color, sub, desc, stats, school, category, debuffs, slotKey }) {
  const iconStyle = color
    ? `background:${color}22;border:1px solid ${color}`
    : "background:rgba(138,134,166,0.2);border:1px solid #8a86a6";
  const statHTML = (stats && stats.length)
    ? `<div class="tip-stats">${stats.map(([k, v]) => `<span><b>${escapeHtml(k)}</b> ${escapeHtml(String(v))}</span>`).join("")}</div>`
    : "";
  const badgeHTML = (school ? schoolTipHTML(school) : "") + (category ? categoryTipHTML(category) : "");
  const debuffHTML = debuffs ? debuffTipHTML(debuffs) : "";
  const effectHTML = debuffs ? debuffEffectsHTML(debuffs) : "";
  const iconBody = iconHTML != null ? iconHTML : icon;
  const slotHTML = slotKey
    ? `<span class="tip-slot-key k">${escapeHtml(slotKey)}</span>`
    : "";
  return slotHTML
    + `<div class="tip-head"><span class="tip-icon" style="${iconStyle}">${iconBody}</span>`
    + `<div class="tip-headtext"><div class="tip-title-row"><div class="tip-name">${escapeHtml(name)}</div>${badgeHTML}</div>`
    + (sub ? `<div class="tip-sub">${escapeHtml(sub)}</div>` : "")
    + debuffHTML
    + `</div></div>`
    + `<div class="tip-desc">${escapeHtml(desc)}</div>`
    + effectHTML
    + statHTML;
}

// Spell tooltip.
export function spellTipHTML(id, slotOverride) {
  const s = SPELLS[id];
  if (!s) return "";
  const stats = spellStatsForTip(id);
  const slotKey = slotOverride ? keyLabelForSlot(slotOverride) : shopCategoryLabel(s.shopCategory);
  const desc = spellDescDetail(id);
  return buildTip({
    icon: s.fallbackIcon,
    iconHTML: spellIconInner(id),
    name: s.name,
    color: s.color,
    desc,
    stats,
    school: s.school,
    category: s.category,
    debuffs: spellDebuffTagsForTip(s),
    slotKey,
  });
}

/** Action-bar tooltip — shows active synergy name/icon when one is live. */
export function spellBarTipHTML(spellId, player) {
  const syn = player && activeSynergyForSpell(
    player.loadout, player.committed, player.passives, spellId,
  );
  if (!syn) return spellTipHTML(spellId);
  const s = SPELLS[spellId];
  if (!s) return "";
  const stats = player
    ? synergyHostStats(spellId, syn.id, player.passives)
    : spellStatsForTip(spellId).map(([k, v]) => [k, String(v)]);
  const partner = syn.requiresSpell && SPELLS[syn.requiresSpell];
  let sub = "Synergy";
  if (partner) sub += ` · pairs with ${partner.name}`;
  return buildTip({
    iconHTML: barIconInner(spellId, syn.id, syn.name),
    name: syn.name,
    color: s.color,
    sub,
    desc: syn.desc || s.desc,
    stats,
    school: s.school,
    category: s.category,
    debuffs: spellDebuffTagsForTip(s),
    slotKey: keyLabelForSlot(slotOfSpell(player.loadout, spellId) || ""),
  });
}

export function itemTipHTML(id, n) {
  const it = ITEMS[id];
  if (!it) return "";
  const stats = [];
  if (it.cost) stats.push(["Cost", it.cost + "g"]);
  if (n > 1) stats.push(["Owned", "×" + n]);
  const sub = it.stackable ? "Passive item · stackable" : "Passive item · unique";
  return buildTip({ icon: it.icon, name: it.name, sub, desc: it.desc, stats });
}

/** Passive / synergy node tooltip (shop tree, patch notes, …). */
export function passiveTipHTML(spellId, passiveId, ctx) {
  const n = (PASSIVE_TREES[spellId] || []).find(p => p.id === passiveId);
  if (!n) return "";
  const host = SPELLS[spellId];
  const owned = ctx?.owned ?? passiveOwned(ctx?.passives, spellId, passiveId);
  let stats = [];
  if (n.synergy) {
    stats = synergyHostStats(spellId, passiveId, ctx?.passives);
    if (!owned && n.cost) stats.unshift(["Cost", `${n.cost} Sy`]);
  } else if (n.cost) {
    stats.push(["Cost", `${n.cost} PP`]);
  }
  let sub = n.synergy ? "Synergy" : "Passive";
  if (n.requiresSpell && SPELLS[n.requiresSpell]) {
    sub += ` · pairs with ${SPELLS[n.requiresSpell].name}`;
  }
  const req = passiveRequiresLabel(n, spellId);
  const desc = req ? `${n.desc} (${req})` : n.desc;
  const name = ctx?.shopLabel ? passiveShopName(n) : n.name;
  return buildTip({
    iconHTML: n.synergy ? synergyIconInner(passiveId, n.name) : spellIconInner(spellId),
    name,
    color: host?.color || "#a855f7",
    sub,
    desc,
    stats,
    school: host?.school,
    category: host?.category,
    debuffs: host ? spellDebuffTagsForTip(host) : null,
  });
}

export function tipContextFromEl(el) {
  const spellId = el.getAttribute("data-tip-spell") || "";
  const game = window.game;
  const human = game?.human?.();
  const passives = human?.passives;
  return { passives, spellId };
}

// HTML attribute string to mark any element as a tooltip target. Handled by the
// document-level delegation wired in wireTooltips().
export function tipAttr(kind, id) {
  return `data-tip-kind="${kind}" data-tip-id="${escapeHtml(id)}"`;
}

export function tipHTMLForEl(el) {
  const kind = el.getAttribute("data-tip-kind");
  const id = el.getAttribute("data-tip-id");
  if (kind === "spell") {
    return spellTipHTML(id, el.getAttribute("data-tip-slot") || undefined);
  }
  if (kind === "item") return itemTipHTML(id, parseInt(el.getAttribute("data-tip-count") || "0", 10) || 0);
  if (kind === "passive") {
    const ctx = tipContextFromEl(el);
    if (el.closest("#shop")) ctx.shopLabel = true;
    return passiveTipHTML(el.getAttribute("data-tip-spell"), id, ctx);
  }
  return "";
}

// Single delegated listener drives every DOM tooltip target. The on-canvas
// action bar is handled separately (it has no DOM nodes).
export function wireTooltips() {
  let current = null;
  document.addEventListener("mousemove", (e) => {
    const game = window.game;
    const x = e.clientX, y = e.clientY;
    // 1234/QWER canvas action bar — Game owns tooltips (no DOM nodes to hover).
    // Must run before shopPopoversBlocked / DOM cleanup so bubble phase never hides bar tips.
    if (game?.overActionBarTip?.(x, y)) return;
    if (window.shopPopoversBlocked?.(x, y)) {
      if (current) { current = null; Tip.hide(); }
      window.SpellPreview?.hide?.();
      return;
    }
    if (e.target && e.target.id === "game") return;
    // Keep DOM tooltips readable while the cursor moves onto the popover.
    if (Tip.isOver(x, y)) return;
    const el = e.target.closest ? e.target.closest("[data-tip-kind]") : null;
    if (!el) { if (current) { current = null; Tip.hide(); } return; }
    if (el.closest("#draft")) {
      if (current) { current = null; Tip.hide(); }
      return;
    }
    if (el.getAttribute("data-tip-kind") === "spell" && el.hasAttribute("data-spell-preview") &&
        window.SpellPreview && SpellPreview.isEnabled()) {
      if (current) { current = null; Tip.hide(); }
      return;
    }
    if (el.getAttribute("data-tip-kind") === "passive" && el.hasAttribute("data-synergy-preview") &&
        window.SpellPreview && SpellPreview.isEnabled()) {
      if (current) { current = null; Tip.hide(); }
      return;
    }
    if (el !== current) { current = el; Tip.show(tipHTMLForEl(el), e.clientX, e.clientY); }
    else scheduleTipMove(e.clientX, e.clientY);
  });
  // hovering out of the window entirely
  document.addEventListener("mouseleave", () => {
    current = null;
    if (window.game) window.game.clearSpellBarTip?.();
    Tip.hide();
    window.SpellPreview?.hide?.();
  });
}

// Hover + click sounds for menus, lobby, shop, and other overlay chrome.
export const UI_SOUND_SEL =
  'button:not(:disabled), .team-chip.clickable, label.sandbox-cfg-check, label.visibility-toggle, label.spell-preview-toggle';
export function uiSoundTarget(el) {
  if (!el) return null;
  const t = el.closest(UI_SOUND_SEL);
  if (!t || t.disabled) return null;
  if (t.closest("#game")) return null;
  if (t.closest("#overlay") || t.closest("#audioWrap") || t.closest("#spectateHud")) return t;
  if (t.id === "scoreboardToggle") return t;
  return null;
}
export let _uiHoverBtn = null;
export function setupUiButtonSounds() {
  if (setupUiButtonSounds.done) return;
  setupUiButtonSounds.done = true;
  document.addEventListener("mouseover", (e) => {
    const el = uiSoundTarget(e.target);
    if (!el || el === _uiHoverBtn) return;
    _uiHoverBtn = el;
    Sound.resume();
    Sound.uiHover();
  });
  document.addEventListener("mouseout", (e) => {
    const el = uiSoundTarget(e.target);
    if (el && el === _uiHoverBtn) _uiHoverBtn = null;
  });
  document.addEventListener("click", (e) => {
    const el = uiSoundTarget(e.target);
    if (!el) return;
    Sound.resume();
    Sound.uiClick();
  }, true);
}

// In-game styled confirm dialog (replaces window.confirm). `body` is HTML.
// Resolves true on confirm, false on cancel / backdrop / Escape.
export function showConfirm({ title, body, okText = "Confirm", cancelText = "Cancel" }) {
  const back = document.getElementById("confirmModal");
  const okBtn = document.getElementById("confirmOk");
  const cancelBtn = document.getElementById("confirmCancel");
  document.getElementById("confirmTitle").textContent = title;
  document.getElementById("confirmBody").innerHTML = body;
  okBtn.textContent = okText;
  cancelBtn.textContent = cancelText;
  back.classList.remove("hidden");

  return new Promise((resolve) => {
    const close = (result) => {
      back.classList.add("hidden");
      okBtn.onclick = cancelBtn.onclick = back.onclick = null;
      document.removeEventListener("keydown", onKey);
      resolve(result);
    };
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      else if (e.key === "Enter") close(true);
    };
    okBtn.onclick = () => close(true);
    cancelBtn.onclick = () => close(false);
    back.onclick = (e) => { if (e.target === back) close(false); };
    document.addEventListener("keydown", onKey);
  });
}
export function ordinal(n) {
  if (!n) return "—";
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Liveness watchdog: if no message (pong or snapshot) arrives for this long,
// treat the socket as dead. The server answers every 2.5s ping with a pong and
// streams snapshots at 20 Hz in-match, so silence this long means the
// connection is gone — typically a half-open socket left behind by a server
// restart/deploy, where the browser never fires `onclose` on its own.
const DEAD_MS = 8000;

// Thin WebSocket wrapper; queues messages until the socket opens.
export class Net {
  constructor(game, onMsg) {
    this.game = game; this.onMsg = onMsg;
    this.ws = null; this.open = false; this.queue = []; this.hb = null;
    this.lastPingSent = 0; // perf.now() of the in-flight ping, for RTT timing
    this.lastRecv = 0;     // perf.now() of the last message received (liveness)
    this.dead = false;     // guards against firing the disconnect path twice
  }
  connect() {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${location.host}/ws`;
    try { this.ws = new WebSocket(url); } catch (e) { this.game.onDisconnect(); return; }
    this.ws.onopen = () => {
      this.open = true;
      this.lastRecv = performance.now();
      this.queue.forEach(m => this.ws.send(m)); this.queue = [];
      // app-level heartbeat keeps idle (lobby/shop) connections warm + alive,
      // doubles as the latency probe (RTT on the matching pong), and drives the
      // liveness watchdog so a dead/half-open socket is detected even when the
      // browser never delivers an onclose (common across a server deploy).
      this.hb = setInterval(() => {
        if (performance.now() - this.lastRecv > DEAD_MS) { this.dropStale(); return; }
        this.sendPing();
      }, 2500);
      this.sendPing();
    };
    this.ws.onmessage = (e) => { this.lastRecv = performance.now(); try { this.onMsg(JSON.parse(e.data)); } catch (err) {} };
    this.ws.onclose = () => { this.open = false; if (this.hb) { clearInterval(this.hb); this.hb = null; } if (!this.dead) { this.dead = true; this.game.onDisconnect(); } };
    this.ws.onerror = () => {};
  }
  // dropStale tears down a connection the watchdog has judged dead and kicks off
  // reconnection directly — `ws.close()` on a half-open socket may not fire
  // onclose for a long time, so we don't wait for it.
  dropStale() {
    if (this.dead) return;
    this.dead = true;
    this.open = false;
    if (this.hb) { clearInterval(this.hb); this.hb = null; }
    if (this.ws) { this.ws.onclose = null; this.ws.onmessage = null; try { this.ws.close(); } catch (e) {} this.ws = null; }
    this.game.onDisconnect();
  }
  send(obj) {
    const s = JSON.stringify(obj);
    if (this.open) { try { this.ws.send(s); } catch (e) {} }
    else this.queue.push(s);
  }
  sendPing() {
    this.lastPingSent = performance.now();
    this.send({ t: "ping" });
  }
  close() {
    this.open = false;
    if (this.hb) { clearInterval(this.hb); this.hb = null; }
    if (this.ws) { this.ws.onclose = null; this.ws.onmessage = null; try { this.ws.close(); } catch (e) {} this.ws = null; }
  }
}
